import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert, Chip, Tooltip,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useJediReleasesGet, JediRelease } from '../../../clients/graphql/pages/release-queries';

export const ReleaseAppsPage = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useJediReleasesGet({});
  const releases = data?.jediReleasesGet?.releases || [];

  const handleRowClick = (params: any) => {
    navigate(`/developer/release/${params.row.releaseKey}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching releases. Please reload the page.
        </Alert>
      </Box>
    );
  }

  const columns = [
    {
      field: 'releaseKey',
      headerName: 'Release Key',
      width: 150,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
    },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Enabled' : 'Disabled'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'percentage',
      headerName: 'Percentage',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left',
          }}
        >
          <Typography variant="body2">
            {params.value != null ? `${params.value}%` : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'whitelist',
      headerName: 'Whitelist',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const users = (params.value as string[]) || []
        const fullText = users.length ? users.join(', ') : 'None'

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            }}
          >
            <Tooltip title={fullText}>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  cursor: users.length ? 'pointer' : 'default',
                }}
              >
                {fullText}
              </Typography>
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left',
          }}
        >
          <Typography variant="body2">
            {new Date(params.value).toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left',
          }}
        >
          <Typography variant="body2">
            {new Date(params.value).toLocaleString()}
          </Typography>
        </Box>
      ),
    },
  ];

  const rows = releases.map((release: JediRelease, index: number) => ({
    id: index,
    ...release,
  }));

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
        Active Releases
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={45}
          onRowClick={handleRowClick}
          sx={{
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
          disableColumnMenu
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};
